/**
 * Log severity levels, ordered from highest to lowest priority.
 * When diagnostic mode is OFF only INFO and above are persisted.
 * When diagnostic mode is ON all levels including DEBUG are persisted.
 */
export type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';

/**
 * Structured context attached to a log entry.
 */
export type LogContext = Record<string, unknown>;

export const LogCategory = {
    APP_LIFECYCLE: 'app.lifecycle',
    AUTH: 'auth',
    NAVIGATION: 'navigation',
    STORAGE: 'storage',
    NETWORK: 'network',
    DIAGNOSTICS: 'diagnostics',
    ENCOUNTER: 'encounter',
    SYNC: 'sync',
} as const;

export type LogCategory = (typeof LogCategory)[keyof typeof LogCategory];

export const SCHEMA_VERSION = 1;

/**
 * A single persisted log entry.
 */
export interface LogEntry {
    id?: number;
    timestamp: string;
    level: LogLevel;
    category: LogCategory;
    event: string;
    context: LogContext;
    message?: string;
    sessionId?: string;
    logSessionId?: string;
    diagnosticMode?: boolean;
    schemaVersion?: number;
    correlationId?: string;
    buildNumber?: string;
    environment?: string;
    offline?: boolean;
    route?: string;
}

export interface LogWriteOptions {
    sessionId?: string;
    logSessionId?: string;
    correlationId?: string;
    schemaVersion?: number;
    buildNumber?: string;
    environment?: string;
    offline?: boolean;
    route?: string;
}


export type OutboxState = 'pending' | 'inflight';

/**
 * A log entry awaiting delivery, independent of the logs table.
 */
export interface OutboxRow {
    id?: number;
    uuid: string;
    level: LogLevel;
    state: OutboxState;
    claimedAt?: number;
    attempts: number;
    createdAt: number;
    record: Omit<LogEntry, 'id'>;
}
